import React, { useState, useMemo } from 'react';
import { Modal, Field, Input, Select, Btn, TipoBadge, MedioBadge, SectionHeader } from '../components/UI';
import { fmtM, fmtDate, todayStr, exportToCSV } from '../utils';
import { showToast } from '../components/UI';

const TIPOS = ['venta','gasto','compra','pago_proveedor','retiro','deposito','prestamo','ingreso_prestamo','acreditacion'];

export default function Historial({ store, setItem, delItem }) {
  const { movimientos } = store;
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroMedio, setFiltroMedio] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;

  const todos = useMemo(() =>
    Object.values(movimientos || {})
      .sort((a, b) => (b.fecha + (b.hora||'')).localeCompare(a.fecha + (a.hora||'')))
  , [movimientos]);

  const TIPOS_OCULTOS = ['ajuste_inicial', 'ajuste_stock', 'saldo_inicial'];

  const filtered = useMemo(() => {
    return todos.filter(m => {
      if (TIPOS_OCULTOS.includes(m.tipo)) return false;
      if (filtroTipo && m.tipo !== filtroTipo) return false;
      if (filtroMes && m.fecha?.slice(0,7) !== filtroMes) return false;
      if (filtroMedio && m.medio !== filtroMedio) return false;
      if (busqueda) {
        const q = busqueda.toLowerCase();
        return (m.desc||'').toLowerCase().includes(q) || (m.proveedor||'').toLowerCase().includes(q) || (m.socio||'').toLowerCase().includes(q);
      }
      return true;
    });
  }, [todos, filtroTipo, filtroMes, filtroMedio, busqueda]);

  const totalPaginas = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);

  // Stats
  const totalEntradas = filtered.filter(m => m.signo === '+').reduce((a, m) => a + (parseFloat(m.monto)||0), 0);
  const totalSalidas = filtered.filter(m => m.signo === '-').reduce((a, m) => a + (parseFloat(m.monto)||0), 0);

  function openEdit(m) {
    setEditForm({ desc: m.desc||'', monto: m.monto||'', fecha: m.fecha||'', medio: m.medio||'', tipo: m.tipo||'' });
    setEditModal(m);
  }

  function saveEdit() {
    const updated = { ...editModal, desc: editForm.desc, monto: parseFloat(editForm.monto)||editModal.monto, fecha: editForm.fecha, medio: editForm.medio };
    setItem('movimientos', editModal.id, updated);
    showToast('Movimiento actualizado');
    setEditModal(null);
  }

  function cancelMovimiento(m) {
    // Create a reversal entry
    const reversalId = 'rev_' + m.id;
    setItem('movimientos', reversalId, {
      id: reversalId, tipo: 'cancelacion', fecha: todayStr(),
      hora: new Date().toTimeString().slice(0,5),
      monto: m.monto, medio: m.medio,
      signo: m.signo === '+' ? '-' : '+',
      desc: `CANCELACIÓN: ${m.desc || m.tipo}`,
      cancelaId: m.id, autor: 'panel'
    });
    // Mark original as cancelled
    setItem('movimientos', m.id, { ...m, cancelado: true, fechaCancelacion: todayStr() });
    showToast('Movimiento cancelado con asiento de reversión');
    setConfirmCancel(null);
  }

  const meses = useMemo(() => {
    const set = new Set(todos.map(m => m.fecha?.slice(0,7)).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [todos]);

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Total movimientos', val: filtered.length + '', color: 'var(--tx)' },
          { label: 'Entradas', val: fmtM(totalEntradas), color: 'var(--grn)' },
          { label: 'Salidas', val: fmtM(totalSalidas), color: 'var(--red)' },
          { label: 'Neto', val: fmtM(totalEntradas - totalSalidas), color: totalEntradas >= totalSalidas ? 'var(--grn)' : 'var(--red)' },
        ].map((s,i) => (
          <div key={i} style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '12px 14px', flex: 1 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Courier, monospace', color: s.color }}>{s.val}</div>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Btn onClick={() => exportToCSV('historial', ['Fecha','Hora','Tipo','Descripcion','Medio','Signo','Monto'],
            filtered.map(m => [m.fecha, m.hora||'', m.tipo, m.desc||'', m.medio||'', m.signo, m.monto]))}>
            ⬇ CSV
          </Btn>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input value={busqueda} onChange={e => { setBusqueda(e.target.value); setPage(1); }}
          placeholder="Buscar descripción, proveedor..."
          style={{ flex: 2, minWidth: 180, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 12, background: 'var(--surf)', color: 'var(--tx)', outline: 'none' }} />
        <select value={filtroTipo} onChange={e => { setFiltroTipo(e.target.value); setPage(1); }}
          style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 12, background: 'var(--surf)', color: 'var(--tx)' }}>
          <option value="">Todos los tipos</option>
          {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
          <option value="cancelacion">cancelacion</option>
        </select>
        <select value={filtroMes} onChange={e => { setFiltroMes(e.target.value); setPage(1); }}
          style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 12, background: 'var(--surf)', color: 'var(--tx)' }}>
          <option value="">Todos los meses</option>
          {meses.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={filtroMedio} onChange={e => { setFiltroMedio(e.target.value); setPage(1); }}
          style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 12, background: 'var(--surf)', color: 'var(--tx)' }}>
          <option value="">Todos los medios</option>
          <option value="efectivo">Efectivo</option>
          <option value="transferencia">Transferencia</option>
          <option value="qr">QR</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden', marginBottom: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surf2)' }}>
              {['Fecha','Hora','Tipo','Descripción','Medio','Monto',''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '9px 13px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0
              ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--tx3)' }}>Sin movimientos con estos filtros</td></tr>
              : paginated.map(m => (
                <tr key={m.id} style={{ borderBottom: '1px solid var(--border)', opacity: m.cancelado ? 0.45 : 1, background: m.cancelado ? 'var(--surf2)' : m.tipo === 'cancelacion' ? 'var(--yel-l)' : '' }}>
                  <td style={{ padding: '8px 13px', fontFamily: 'Courier, monospace', fontSize: 11, color: 'var(--tx3)', whiteSpace: 'nowrap' }}>{fmtDate(m.fecha)}</td>
                  <td style={{ padding: '8px 13px', fontFamily: 'Courier, monospace', fontSize: 10, color: 'var(--tx3)' }}>{m.hora || '—'}</td>
                  <td style={{ padding: '8px 13px', whiteSpace: 'nowrap' }}>
                    <TipoBadge tipo={m.tipo} />
                    {m.cancelado && <span style={{ marginLeft: 5, fontSize: 9, color: 'var(--red)', fontWeight: 700 }}>CANCELADO</span>}
                  </td>
                  <td style={{ padding: '8px 13px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.desc || '—'}</td>
                  <td style={{ padding: '8px 13px', whiteSpace: 'nowrap' }}><MedioBadge medio={m.medio} /></td>
                  <td style={{ padding: '8px 13px', fontFamily: 'Courier, monospace', fontWeight: 700, whiteSpace: 'nowrap', color: m.signo === '+' ? 'var(--grn)' : 'var(--red)' }}>
                    {m.signo === '+' ? '+' : ''}{fmtM(m.monto)}
                  </td>
                  <td style={{ padding: '8px 13px' }}>
                    {!m.cancelado && m.tipo !== 'cancelacion' && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Btn size="sm" onClick={() => openEdit(m)}>✏</Btn>
                        <Btn size="sm" variant="red" onClick={() => setConfirmCancel(m)}>↩</Btn>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPaginas > 1 && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', marginBottom: 16 }}>
          <Btn size="sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>← Anterior</Btn>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>Página {page} de {totalPaginas} ({filtered.length} registros)</span>
          <Btn size="sm" onClick={() => setPage(p => Math.min(totalPaginas, p+1))} disabled={page === totalPaginas}>Siguiente →</Btn>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <Modal title="Editar movimiento" onClose={() => setEditModal(null)} onSave={saveEdit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            <Field label="Descripción" full>
              <Input value={editForm.desc} onChange={v => setEditForm(f => ({...f, desc: v}))} autoFocus />
            </Field>
            <Field label="Monto">
              <Input value={editForm.monto} onChange={v => setEditForm(f => ({...f, monto: v}))} type="number" />
            </Field>
            <Field label="Fecha">
              <Input value={editForm.fecha} onChange={v => setEditForm(f => ({...f, fecha: v}))} type="date" />
            </Field>
            <Field label="Medio">
              <Select value={editForm.medio} onChange={v => setEditForm(f => ({...f, medio: v}))}>
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="qr">QR</option>
              </Select>
            </Field>
          </div>
          <div style={{ marginTop: 12, padding: '9px 12px', background: 'var(--yel-l)', borderRadius: 8, fontSize: 11, color: 'var(--yel)' }}>
            ⚠ Editás el registro directamente. Si el monto cambió mucho, es mejor cancelar y cargar uno nuevo.
          </div>
        </Modal>
      )}

      {/* Cancel confirmation */}
      {confirmCancel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}
          onClick={e => { if (e.target === e.currentTarget) setConfirmCancel(null); }}>
          <div style={{ background: 'var(--surf)', borderRadius: 'var(--rl)', padding: 24, width: 400, boxShadow: '0 8px 40px rgba(0,0,0,.15)' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Cancelar movimiento</h3>
            <div style={{ background: 'var(--surf2)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12 }}>
              <div style={{ fontWeight: 600 }}>{confirmCancel.desc || confirmCancel.tipo}</div>
              <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, color: confirmCancel.signo === '+' ? 'var(--grn)' : 'var(--red)', marginTop: 4 }}>
                {confirmCancel.signo === '+' ? '+' : ''}{fmtM(confirmCancel.monto)} — {fmtDate(confirmCancel.fecha)}
              </div>
            </div>
            <div style={{ background: 'var(--yel-l)', borderRadius: 8, padding: '9px 12px', marginBottom: 16, fontSize: 11, color: 'var(--yel)' }}>
              Se crea un asiento de reversión que anula el efecto en caja. El movimiento original queda visible como cancelado.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn onClick={() => setConfirmCancel(null)}>Cancelar</Btn>
              <Btn variant="red" onClick={() => cancelMovimiento(confirmCancel)}>Confirmar cancelación</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
